import { ArrowForward, Close, Email, Phone, Place } from "@mui/icons-material";
import CrisisAlertIcon from "@mui/icons-material/CrisisAlert";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import LockIcon from "@mui/icons-material/Lock";
import { Avatar, Box, Button, Grid, Stack, Typography } from "@mui/material";
import { isBefore } from "date-fns";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { gateway } from "../gateway";
import { appStates, setAppState } from "../redux/features/appStateSlice";
import { resetConnectBusinessState } from "../redux/features/connectBusinessStateSlice";
import { EBenefitTypes, generateRandomPhoneNumber, getBasePath } from "../utils";
import { BaseDialog } from "./common/BaseDialog";
import { connectBusinessFlowColors, connectBusinessFlowSx } from "./connectBusinessFlowStyles";
import { LoaderComponent } from "./Loader";
import { TravelingCard } from "./TravelingCard";
import { Dialog } from "./Dialog";
import { useDialog } from "./Dialog/hooks/useDialog";

const styles = {
  titleSx: {
    fontWeight: "bold",
  },
  titleConnectionSx: {
    textAlign: "center",
    color: "blue",
    fontSize: "1.2em",
  },
  actionSx: {
    justifyContent: "center",
  },
  btnCloseSx: {
    backgroundColor: "grey",
    color: "white",
    "&:hover": { backgroundColor: "var(--fx-surface-sidebar, #233044)" },
    margin: "0 8px",
  },
  btnAcceptSx: {
    backgroundColor: "#3c7cde",
    color: "white",
    "&:hover": { backgroundColor: "var(--fx-surface-sidebar, #233044)" },
    margin: "0 8px",
  },
};

export const EmployeeBasicCard = ({
  uid,
  avatar,
  firstName,
  lastName,
  country,
  state,
  city,
  distances,
  children,
  isInOwnedCoverage = false,
  isConnected,
}) => {
  const userState = useSelector((state) => state.userState);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const [openConfirmDialog, setOpenConfirmDialog] = React.useState(false);
  const [openInsufficientBenefits, setOpenInsufficientBenefits] = React.useState(false);
  const [openNotInYourCoverages, setOpenNotInYourCoverages] = React.useState(false);
  const demoDialog = useDialog();
  const roleType = userState?.roles[0]?.type;
  const basePath = getBasePath(roleType);

  const benefitType = EBenefitTypes.FINDACLEANER;

  const usersBenefits =
    userState?.benefits?.findLast((benefit) => {
      if (
        benefit?.type === benefitType &&
        benefit?.config?.limitedUsage > (benefit?.CompanyBenefits?.usageCount ?? 0) &&
        isBefore(new Date(), benefit?.CompanyBenefits?.expDate ?? new Date())
      ) {
        return true;
      }
      return false;
    }) ?? null;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleOpenConfirmDialog = () => {
    setOpenConfirmDialog(true);
  };

  const handleOpenInsufficientBenefitsDialog = () => {
    setOpenInsufficientBenefits(true);
  };

  const handleOpenNotInYourCoveragesDialog = () => {
    setOpenNotInYourCoverages(true);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };

  const handleAcceptConfirmDialog = async () => {
    setOpenConfirmDialog(false);
    // Aquí la lógica de conexión, seguida de abrir el diálogo de conexión exitosa
    try {
      setLoading(true);
      await handleConnect();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      setOpenDialog(true);
    }
  };

  const handleOpenDialogFactory = () => {
    //TODO: IMPORTANT: Discoment this to use benefits limit feat
    if (!isInOwnedCoverage) {
      return handleOpenNotInYourCoveragesDialog;
    } else if ((usersBenefits?.CompanyBenefits?.usageCount ?? 0) < usersBenefits?.config?.limitedUsage) {
      return handleOpenConfirmDialog;
    } else {
      return handleOpenInsufficientBenefitsDialog;
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    dispatch(resetConnectBusinessState());
    navigate(basePath);
  };
  const token = sessionStorage.getItem("token");
  const connectBusinessState = useSelector((state) => state.connectBusinessState);
  const connectBusinessService =
    connectBusinessState.serviceType === "residential" ? connectBusinessState.residentialServices : connectBusinessState.commercialServices;

  const connectBusinessRequirements = connectBusinessState.requirements;

  const serviceIds = Object.entries(connectBusinessService || {})
    .filter(([, isChecked]) => isChecked)
    .map(([id]) => encodeURIComponent(id));

  const servicesParam = serviceIds.join(",");

  const requirementsIds = connectBusinessRequirements.map((item) => {
    return encodeURIComponent(item.id);
  });
  const requirementsParams = requirementsIds.join(",");

  const handleConnect = async () => {
    if (connectBusinessState.isDemo) {
      demoDialog.handleOpen();
      return;
    }
    try {
      handleOpenConfirmDialog(); // Abre el diálogo después de la lógica de conexión
      // alert(servicesParam);
      // alert(JSON.stringify(connectPayload));
      const res = await gateway.get(
        `/connect/employee/${uid}/self?expires=${48}${servicesParam?.length > 0 ? "&services=" + encodeURIComponent(servicesParam) : ""
        }${requirementsParams.length > 0 ? "&requirements=" + encodeURIComponent(requirementsParams) : ""}${connectBusinessState.address ? "&address=" + encodeURIComponent(connectBusinessState.address) : ""
        }${connectBusinessState.country ? "&country=" + encodeURIComponent(connectBusinessState.country) : ""}${connectBusinessState.state ? "&state=" + encodeURIComponent(connectBusinessState.state) : ""
        }${connectBusinessState.city ? "&city=" + encodeURIComponent(connectBusinessState.city) : ""}${connectBusinessState.zipcode ? "&zipcode=" + encodeURIComponent(connectBusinessState.zipcode) : ""
        }${connectBusinessState.lat ? "&lat=" + encodeURIComponent(connectBusinessState.lat) : ""}${connectBusinessState.lng ? "&lng=" + encodeURIComponent(connectBusinessState.lng) : ""
        }`,
        {
          headers: {
            Authorization: `${token}`,
          },
        },
      );
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Box
      sx={{
        width: 300,
        // height: 450,
        mx: "auto",
        p: 3,
        boxShadow: "none",
        borderRadius: "3px",
        border: `1px solid ${connectBusinessFlowColors.border}`,
        backgroundColor: "var(--fx-surface-base, #FFFFFF)",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        mt: 3,
        mb: 5,
        position: "relative",
      }}
    >
      {children}
      <Grid container spacing={2} direction="row" alignItems="center" justifyContent="center">
        {" "}
        <Grid item xs={12} display="flex" justifyContent="center">
          {" "}
          <Avatar alt={avatar} src={avatar} sx={{ width: 100, height: 100 }} />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ fontWeight: "medium", textAlign: "center" }}>
            {" "}
            {firstName} {lastName}
          </Typography>
        </Grid>
      </Grid>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          // alignItems: 'center',
          gap: 0.5,
          marginTop: "10px",
          // marginInline: "10px",
        }}
      >
        <Stack direction="row" gap={1}>
          <Place color="action" />
          {city}, {state}, {country}
        </Stack>
        {/* <Stack direction="row" gap={1} sx={{ mt: 2 }}>
          <Work color="action" />
        </Stack> */}
        <Stack direction="row" gap={1} sx={{ mt: 2, position: "relative" }}>
          <Email color="action" />
          <Typography
            sx={{
              filter: "blur(3px)", // Ajusta el valor según la cantidad de desenfoque que deseas
            }}
          >
            email@gmail.com
          </Typography>

          <LockIcon sx={{ position: "absolute", right: 30 }} color="action"></LockIcon>
        </Stack>
        <Stack direction="row" gap={1} sx={{ mt: 2, position: "relative" }}>
          <Phone color="action" />
          <Typography
            sx={{
              filter: "blur(3px)", // Ajusta el valor según la cantidad de desenfoque que deseas
            }}
          >
            {generateRandomPhoneNumber()}
          </Typography>

          <LockIcon sx={{ position: "absolute", right: 30 }} color="action"></LockIcon>
        </Stack>
        <Stack direction="row" gap={1} sx={{ mt: 2 }}>
          <Grid container sx={{}}>
            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <TravelingCard
                icon={<DirectionsCarIcon></DirectionsCarIcon>}
                duration={distances?.driving?.duration}
                distance={distances?.driving?.distance}
              ></TravelingCard>
            </Grid>
          </Grid>
        </Stack>
      </Box>

      <Button
        variant="contained"
        onClick={() => {
          const fc = handleOpenDialogFactory();
          fc();
        }}
        disabled={isConnected}
        sx={{
          width: "100%",
          height: "30px",
          backgroundColor: isInOwnedCoverage || connectBusinessState.isDemo ? connectBusinessFlowColors.accent : "#737373",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginTop: 2,
          borderRadius: "3px",
          boxShadow: "none",
          textTransform: "none",
          fontWeight: 600,
          "&:hover": {
            backgroundColor: isInOwnedCoverage || connectBusinessState.isDemo ? connectBusinessFlowColors.accentDark : "#737373",
            boxShadow: "none",
          },
          "&.Mui-disabled": {
            backgroundColor: "#737373",
            color: "#ffff",
          },
        }}
      >
        {isConnected ? "ALREADY CONNECTED" : "CONNECT"}
      </Button>

      <BaseDialog
        openState={openInsufficientBenefits}
        setOpenState={setOpenInsufficientBenefits}
        title="Benefits Limit Reached!"
        titleSx={styles.titleSx}
        content="You don't have the required benefits or you've exceeded the usage limit to perform this action. Please review your plan for details or contact support for assistance."
        actionSx={styles.actionSx}
        actions={[
          {
            label: "Close",
            onClick: () => {
              setOpenInsufficientBenefits(false);
            },
            style: styles.btnCloseSx,
            icon: <ArrowForward></ArrowForward>,
          },
          {
            label: "Go to plans",
            onClick: () => {
              navigate(`${basePath}/plan-center`);
              dispatch(resetConnectBusinessState());
              dispatch(
                setAppState({
                  currState: appStates.PLANS,
                }),
              );
            },
            style: styles.btnAcceptSx,
            icon: <Close></Close>,
          },
        ]}
      ></BaseDialog>

      <BaseDialog
        openState={openConfirmDialog}
        setOpenState={setOpenConfirmDialog}
        title="Are you sure you want to connect with this cleaner?"
        content="This will use one of your available connections from your plan."
        actionSx={styles.actionSx}
        actions={[
          {
            label: "Cancel",
            onClick: () => {
              handleCloseConfirmDialog();
            },
            style: styles.btnCloseSx,
          },
          {
            label: "Accept",
            onClick: () => {
              handleAcceptConfirmDialog();
            },
            style: styles.btnAcceptSx,
          },
        ]}
      ></BaseDialog>

      <BaseDialog
        openState={openNotInYourCoverages}
        setOpenState={setOpenNotInYourCoverages}
        title="Cleaner is not located in your coverage areas!"
        titleSx={styles.titleSx}
        content="The current cleaner is not in any of you coverages areas, set a new coverage area to connect with him/her or update the alrready coverages on you account."
        actionSx={styles.actionSx}
        actions={[
          {
            label: "Close",
            onClick: () => {
              setOpenNotInYourCoverages(false);
            },
            style: styles.btnCloseSx,
            icon: <Close></Close>,
          },
          {
            label: "Go to coverages areas",
            onClick: () => {
              if (connectBusinessState.isDemo) {
                setOpenNotInYourCoverages(false);
                demoDialog.handleOpen();
                return;
              }

              const targetPath = basePath === "/" ? "/coverage-areas" : `${basePath}/coverage-areas`;
              navigate(targetPath);
              dispatch(resetConnectBusinessState());
              dispatch(
                setAppState({
                  currState: appStates.COVERAGES,
                }),
              );
            },
            style: styles.btnAcceptSx,
            icon: <CrisisAlertIcon></CrisisAlertIcon>,
          },
        ]}
      ></BaseDialog>

      <BaseDialog
        openState={openDialog}
        setOpenState={handleCloseDialog}
        disableScape={true}
        title="SUCCESSFUL CONNECTION"
        titleSx={styles.titleConnectionSx}
        content={
          <Typography sx={{ fontWeight: "bold", textAlign: "center", marginTop: "20px", color: "var(--fx-surface-text, #0F172A)" }}>
            We have sent you an email to continue with the connection process.
          </Typography>
        }
        actionSx={styles.actionSx}
        actions={[
          {
            label: "Return Home",
            onClick: () => {
              handleCloseDialog();
              dispatch(
                setAppState({
                  currState: appStates.HOME,
                }),
              );
            },
            style: styles.btnCloseSx,
            autoFocus: true,
          },
        ]}
      ></BaseDialog>

      <LoaderComponent open={loading} type="spinner"></LoaderComponent>

      <Dialog
        open={demoDialog.open}
        onClose={demoDialog.handleClose}
        styles={{ zIndex: 99999 }}
        primaryButton={{
          label: "Go to Sign In",
          onClick: () => {
            demoDialog.handleClose();
            navigate("/signin");
          },
          variant: "primary",
        }}
        secondaryButton={{
          label: "Close",
          onClick: demoDialog.handleClose,
          variant: "secondary",
        }}
      >
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h5" sx={{ ...connectBusinessFlowSx.title, mb: 2 }}>
            Demo Mode
          </Typography>
          <Typography sx={connectBusinessFlowSx.copy}>
            You are currently in demo mode. Please sign in to connect with our professionals and unlock all features.
          </Typography>
        </Box>
      </Dialog>
    </Box>
  );
};
